// frontend/src/components/MCDocumentVerificationModal.js
// Dedicated MC Document Verification Modal for Outpatient Sick Leave & Hospitalisation Leave
// Follows the NSDocumentVerificationModal pattern — self-contained, reusable, single-responsibility.
// Used on both Dashboard (Pending Verification) and LeaveManagement (Application Row).

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  XCircle,
  Eye,
  FileText,
  User,
  Calendar,
  Shield,
  Stethoscope,
  CheckCircle,
  AlertTriangle,
  Info,
  Clock,
  ZoomIn,
  ZoomOut,
  RefreshCw,
  RotateCw,
  Download,
  Thermometer,
  Hospital,
  Wallet
} from 'lucide-react';
import { staffAPI } from '../services/api';
import api from '../services/api';
import toast from '../utils/toast';
import Portal from './UI/Portal';
import ConfirmModal from './UI/ConfirmModal';
import LoadingSpinner from './UI/LoadingSpinner';
import { InfoBadge } from './UI/InfoBadge';
import { LeaveTypeBadge } from './UI/LeaveTypeBadge';
import { formatDate, parseLocalDate, toLocalISODate } from '../utils/dateUtils';
import { formatLeaveDays, pluralizeDays } from '../utils/numberUtils';
import { describeDuplicateMcNotice } from '../utils/duplicateMcNotice';
import { getUnpaidConsumption } from '../utils/leaveDisplayUtils';
import { isHeicFile, convertHeicBlobToJpeg, getPreviewDocumentType, inferContentType } from '../utils/imageUtils';
import { useIsMobile } from '../hooks/useIsMobile';
import { useImageViewer } from '../hooks/useImageViewer';
import { useSafeDiscardAndClose } from '../hooks/useSafeDiscardAndClose';
import MobileVerificationTabs from './UI/MobileVerificationTabs';
import ExtractAgainButton from './UI/ExtractAgainButton';
import VerificationPanelHeader from './UI/VerificationPanelHeader';
import DatePickerField from './UI/DatePickerField';
import PdfViewer from './UI/PdfViewer';
import LeaveHalfDaySegmentedControl from './Leave/LeaveHalfDaySegmentedControl';
import LeavePeriodDisplay from './Leave/LeavePeriodDisplay';

// ─── Name-matching helper ─────────────────────────────────────────────────────
// Normalise → tokenise → sort → compare sets.
// Handles: all-caps OCR output, extra whitespace, OCR punctuation noise (commas,
// periods), S/O and D/O particles (strips the slash so "S/O" → "SO" stays as one
// token rather than splitting into two), and first/last name transposition.
// Strict set-size equality prevents subset false-positives ("TAN WEI" ≠ "TAN WEI LIANG").
const nameTokensMatch = (a, b) => {
  if (!a || !b) return false;
  const tokenise = (s) =>
    s.toUpperCase()
      .replace(/[^A-Z0-9\s]/g, ' ')  // strip punctuation (/, -, ,, . etc.) → space
      .replace(/\s+/g, ' ')           // collapse runs of whitespace
      .trim()
      .split(' ')
      .filter(Boolean)
      .sort();
  const tokA = tokenise(a);
  const tokB = tokenise(b);
  return tokA.length === tokB.length && tokA.every((t, i) => t === tokB[i]);
};

const DUPLICATE_NOTICE_STYLES = {
  error: {
    box: 'bg-red-50 border-red-200',
    icon: 'text-red-600',
    text: 'text-red-800',
    Icon: AlertTriangle,
  },
  warning: {
    box: 'bg-amber-50 border-amber-200',
    icon: 'text-amber-600',
    text: 'text-amber-800',
    Icon: AlertTriangle,
  },
  info: {
    box: 'bg-blue-50 border-blue-200',
    icon: 'text-blue-600',
    text: 'text-blue-800',
    Icon: Info,
  },
};

function DuplicateMcNotice({ notice }) {
  const style = DUPLICATE_NOTICE_STYLES[notice.tone] || DUPLICATE_NOTICE_STYLES.error;
  const NoticeIcon = style.Icon;
  return (
    <div className={`border rounded-lg p-3 ${style.box}`}>
      <div className="flex items-start gap-2">
        <NoticeIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${style.icon}`} />
        <div className={`text-xs space-y-1 ${style.text}`}>
          <p className="font-semibold">{notice.title}</p>
          <p>{notice.body}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const MCDocumentVerificationModal = ({
  isOpen,
  onClose,
  applicationId,
  application = null,       // Optional pre-loaded leave application data
  onVerificationComplete,
  isHRInitiated = false,    // True when opened via hr_initiate_mc_leave — enables draft discard on close
}) => {
  // ── Internal state ──────────────────────────────────────────────────────────
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState('document');
  const [mcOcrData, setMcOcrData] = useState(null);
  const [loadingMcData, setLoadingMcData] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [rerunningOcr, setRerunningOcr] = useState(false);
  const [daysDraft, setDaysDraft] = useState('');
  const daysDraftTouchedRef = useRef(false);
  const { isDiscarding, safeDiscardAndClose } = useSafeDiscardAndClose();

  // Tracks whether the verification was finalised (verified or rejected).
  // When true, closing the modal skips the confirmation dialog.
  const wasFinalisedRef = useRef(false);

  // Document preview (matches DocumentVerificationModal pattern)
  const [documentBlobUrl, setDocumentBlobUrl] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState(null);
  const blobUrlRef = useRef(null);
  const documentAbortControllerRef = useRef(null);
  const viewer = useImageViewer();

  // Derive leave type information from either the API response or the pre-loaded application
  const leaveTypeName = application
    ? (application.leave_type?.leave_type_name || application.leave_type_id?.leave_type_name || application.leave_type_name || application.leave_type || '').trim()
    : '';

  // Derive leave status — prefer fresh data from verification API, fall back to application prop
  const leaveStatus = mcOcrData?.employee_data?.leave_status || application?.leave_status || '';
  const storedRejectionReason = String(
    mcOcrData?.employee_data?.rejection_reason
    || application?.leave_rejection_reason
    || ''
  ).replace(/^MC rejected:\s*/i, '').trim();

  // True when the linked leave application no longer exists (document was uploaded against a
  // subsequently-deleted leave application).
  const isOrphanedMc = mcOcrData?.orphaned === true;

  // Determine MC upload state from multiple possible field shapes
  const hasUploadedMc = !!(
    application?.has_medical_certificate ||
    application?.has_mc_uploaded ||
    application?.mc_uploaded ||
    (application?.leave_medical_cert_path && application.leave_medical_cert_path.trim()) ||
    mcOcrData?.mc_file_path ||
    mcOcrData?.document_file
  );

  // ── Derived display data (must be declared BEFORE any useEffect that references them) ─────
  const employeeName = mcOcrData?.employee_data?.full_name || application?.employee_name || application?.staff_name || 'Employee';
  const employeeStaffCode = mcOcrData?.employee_data?.staff_id || mcOcrData?.employee_data?.staff_code || application?.staff_code || application?.employee_id || '';
  const mcFilePath = mcOcrData?.mc_file_path || mcOcrData?.document_file || null;
  const extractedData = mcOcrData?.extracted_data || null;
  const employeeData = mcOcrData?.employee_data || {};

  const displayLeaveTypeName = leaveTypeName || employeeData.leave_type_name || 'Sick Leave';
  const displayStartDate = employeeData.leave_start_date || application?.leave_start_date || '';
  const displayEndDate = employeeData.leave_end_date || application?.leave_end_date || '';
  // Schedule-aware figure that ACTUALLY gets deducted (working days for
  // both OSL and Hospitalisation Leave). NEVER fall back to the raw
  // doctor-stated count (ocr_mc_total_days) — that is a calendar span
  // and ignores rest days / public holidays, so it would over-state the
  // deduction.
  const displayTotalDays = employeeData.leave_total_days ?? application?.leave_total_days ?? application?.leave_days ?? '';

  // Backend-supplied breakdown explaining calendar span vs deductible days and
  // which dates (rest days / public holidays) are excluded from the deduction.
  const deductionBreakdown = mcOcrData?.leave_deduction_breakdown || null;
  const excludedDates = deductionBreakdown?.excluded_dates || [];
  const ocrStatedDays = deductionBreakdown?.ocr_stated_days ?? null;
  const startPeriod = employeeData.leave_start_time_period || 'AM';
  const endPeriod = employeeData.leave_end_time_period || 'PM';
  const isSessionAdjusted = Boolean(
    deductionBreakdown?.is_session_adjusted
    || startPeriod !== 'AM'
    || endPeriod !== 'PM'
  );
  const fullDayDeductible = deductionBreakdown?.full_day_deductible_days ?? null;
  const canEditDeduction = ['submitted', 'draft'].includes(leaveStatus);

  const sickLeaveOverlap = mcOcrData?.sick_leave_overlap || null;
  const duplicateMcNumber = mcOcrData?.duplicate_mc_number || null;
  const duplicateMcNotice = duplicateMcNumber
    ? describeDuplicateMcNotice(duplicateMcNumber, {
        currentStatus: leaveStatus,
        currentStartDate: displayStartDate,
        currentEndDate: displayEndDate,
      })
    : null;
  const effectiveDateAdjustment = mcOcrData?.effective_date_adjustment || null;
  const documentIssue = mcOcrData?.document_issue || null;
  const hasBlockingDocumentIssue = Boolean(documentIssue);
  const isDocumentUnavailable = mcOcrData?.document_available === false;

  // Backend preview of the exact approval allocation. For HL extensions, paid
  // HL is consumed first, then scheduled working days use AL, with UL only as
  // the final shortfall. Rest days and public holidays remain MC-covered but
  // consume no AL/UL entitlement.
  const consumptionPreview = mcOcrData?.consumption_preview || null;
  const hasMixedAllocation = !!(
    consumptionPreview &&
    (consumptionPreview.annual_leave_days > 0 || consumptionPreview.unpaid_days > 0)
  );
  const effectiveCoverageDays = consumptionPreview?.total_days ?? displayTotalDays;

  // Actual booked split for an already-approved sick leave (from the stored
  // consumption proposal). Lets the modal show the real pay outcome — paid
  // sick-leave days vs Unpaid Leave — after verification, not just the preview.
  const consumptionProposal = mcOcrData?.consumption_proposal || application?.leave_consumption_proposal || null;
  const unpaidInfo = consumptionProposal
    ? getUnpaidConsumption({ leave_consumption_proposal: consumptionProposal, leave_total_days: displayTotalDays })
    : null;
  const bookedItems = Array.isArray(consumptionProposal?.items) ? consumptionProposal.items : [];
  const bookedSickDays = bookedItems
    .filter((item) => item.leave_type === displayLeaveTypeName)
    .reduce((sum, item) => sum + Number(item.days || 0), 0);
  const bookedAnnualDays = bookedItems
    .filter((item) => String(item.leave_type || '').startsWith('Annual Leave'))
    .reduce((sum, item) => sum + Number(item.days || 0), 0);
  const bookedUnpaidDays = bookedItems
    .filter((item) => String(item.leave_type || '').toLowerCase().includes('unpaid'))
    .reduce((sum, item) => sum + Number(item.days || 0), 0);
  const hasBookedMixedAllocation = bookedAnnualDays > 0 || bookedUnpaidDays > 0;
  const bookedAllocationSummary = [
    bookedSickDays > 0 ? `${formatLeaveDays(bookedSickDays)} ${displayLeaveTypeName}` : null,
    bookedAnnualDays > 0 ? `${formatLeaveDays(bookedAnnualDays)} Annual Leave` : null,
    bookedUnpaidDays > 0 ? `${formatLeaveDays(bookedUnpaidDays)} Unpaid Leave` : null,
  ].filter(Boolean).join(' + ');

  const isSickLeaveType = displayLeaveTypeName.toLowerCase().includes('outpatient') ||
                           displayLeaveTypeName.toLowerCase().includes('hospitalisation') ||
                           displayLeaveTypeName.toLowerCase().includes('sick');

  const isHospitalisationLeave = displayLeaveTypeName.toLowerCase().includes('hospitalisation');
  const LeaveTypeIcon = isHospitalisationLeave ? Hospital : Thermometer;

  // ── Fetch MC verification data ─────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !applicationId) return;

    const loadMcData = async () => {
      setLoadingMcData(true);
      try {
        const response = await staffAPI.getMcVerificationData(applicationId);
        if (response.data?.success !== false) {
          setMcOcrData(response.data);
        }
      } catch (error) {
        const status = error?.response?.status;
        if (status === 404) {
          // MC not uploaded or verification record not ready yet — that's okay
        } else {
          console.error('Failed to load MC OCR data:', error);
          toast.error('Failed to load MC verification data');
        }
      } finally {
        setLoadingMcData(false);
      }
    };

    loadMcData();
  }, [isOpen, applicationId]);

  // Poll for OCR data when the modal is open but extracted_data hasn't
  // arrived yet (e.g. backend proxy or n8n webhook hasn't persisted it by
  // the time the initial fetch ran).  Stops once data appears or after ~30s.
  useEffect(() => {
    if (!isOpen || !applicationId) return;
    if (!hasUploadedMc) return;
    if (hasBlockingDocumentIssue) return;
    if (extractedData) return;       // already have data
    if (loadingMcData) return;       // initial load still in progress

    let cancelled = false;
    let attempt = 0;
    const maxAttempts = 6;
    const intervalMs = 5000;

    const poll = async () => {
      while (!cancelled && attempt < maxAttempts) {
        await new Promise(r => setTimeout(r, intervalMs));
        if (cancelled) break;
        attempt++;
        try {
          const resp = await staffAPI.getMcVerificationData(applicationId);
          if (resp.data?.extracted_data && !cancelled) {
            setMcOcrData(resp.data);
            break;
          }
        } catch { /* retry */ }
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [isOpen, applicationId, hasUploadedMc, hasBlockingDocumentIssue, extractedData, loadingMcData]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMcOcrData(null);
      setLoadingMcData(false);
      setSubmitting(false);
      setDocumentBlobUrl(null);
      setDocumentError(null);
      viewer.reset();
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      if (documentAbortControllerRef.current) {
        documentAbortControllerRef.current.abort();
      }
    }
  }, [isOpen, viewer.reset]);

  // Load document blob when mcFilePath is available (DocumentVerificationModal pattern)
  const loadDocumentBlob = async (filePath) => {
    if (documentAbortControllerRef.current) {
      documentAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    documentAbortControllerRef.current = abortController;

    try {
      setLoadingDocument(true);
      setDocumentError(null);
      viewer.reset();
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }

      if (!filePath) throw new Error('Missing document file path');
      let apiPath = filePath;
      if (filePath.includes('/view/')) {
        const urlParts = filePath.split('/view/');
        apiPath = urlParts[urlParts.length - 1];
      } else if (filePath.startsWith('/documents/view/')) {
        apiPath = filePath.replace('/documents/view/', '');
      }

      const response = await api.get(`/documents/view/${encodeURIComponent(apiPath)}/`, {
        responseType: 'blob',
        signal: abortController.signal
      });

      if (abortController.signal.aborted) return;

      const fileExt = (apiPath.split('.').pop() || '').toLowerCase();
      const inferredType = inferContentType(fileExt, response.headers?.['content-type']);
      let blob = new Blob([response.data], { type: inferredType });

      if (isHeicFile(apiPath)) {
        blob = await convertHeicBlobToJpeg(blob);
      }

      const blobUrl = URL.createObjectURL(blob);

      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = blobUrl;
      setDocumentBlobUrl(blobUrl);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Failed to load document:', error);
      setDocumentError('Failed to load document preview');
    } finally {
      setLoadingDocument(false);
    }
  };

  useEffect(() => {
    if (mcFilePath && isOpen && !isDocumentUnavailable) {
      loadDocumentBlob(mcFilePath);
    }
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      if (documentAbortControllerRef.current) {
        documentAbortControllerRef.current.abort();
      }
    };
  }, [mcFilePath, isOpen, isDocumentUnavailable]);

  const getDocumentType = () => getPreviewDocumentType(mcFilePath);
  const canVerifyMc = Boolean(
    mcOcrData?.verification_ready &&
    documentBlobUrl &&
    !documentError &&
    !hasBlockingDocumentIssue
  );

  const resolvedDeductibleDays = consumptionPreview?.total_days ?? displayTotalDays;

  useEffect(() => {
    if (daysDraftTouchedRef.current) return;
    if (resolvedDeductibleDays === '' || resolvedDeductibleDays == null) return;
    setDaysDraft(String(resolvedDeductibleDays));
  }, [resolvedDeductibleDays]);

  const mergeDeductionSync = useCallback((res) => {
    if (!res?.data) return;
    const synced = res.data.leave_dates_synced;
    setMcOcrData((prev) => ({
      ...prev,
      extracted_data: synced
        ? {
            ...prev?.extracted_data,
            ocr_mc_total_days: synced.leave_total_days ?? prev?.extracted_data?.ocr_mc_total_days,
          }
        : prev?.extracted_data,
      employee_data: {
        ...prev?.employee_data,
        ...(synced || {}),
      },
      leave_deduction_breakdown:
        res.data.leave_deduction_breakdown || prev?.leave_deduction_breakdown,
      consumption_preview:
        res.data.consumption_preview !== undefined
          ? res.data.consumption_preview
          : prev?.consumption_preview,
      effective_date_adjustment: res.data.effective_date_adjustment || null,
    }));
    if (synced?.leave_total_days != null) {
      daysDraftTouchedRef.current = false;
      setDaysDraft(String(synced.leave_total_days));
    }
  }, []);

  const applyDeductionUpdate = useCallback(async (payload) => {
    const res = await staffAPI.updateMcDeduction({
      application_id: applicationId,
      ...payload,
    });
    mergeDeductionSync(res);
    return res;
  }, [applicationId, mergeDeductionSync]);

  const handleSessionChange = useCallback(async (which, period) => {
    const field = which === 'start' ? 'leave_start_time_period' : 'leave_end_time_period';
    const previous = which === 'start' ? startPeriod : endPeriod;
    if (period === previous) return;
    setMcOcrData((prev) => ({
      ...prev,
      employee_data: {
        ...prev?.employee_data,
        [field]: period,
      },
    }));
    try {
      await applyDeductionUpdate({ [field]: period });
    } catch (error) {
      setMcOcrData((prev) => ({
        ...prev,
        employee_data: {
          ...prev?.employee_data,
          [field]: previous,
        },
      }));
      toast.error(error?.response?.data?.error || 'Failed to update leave session');
    }
  }, [applyDeductionUpdate, startPeriod, endPeriod]);

  const handleDaysCommit = useCallback(async () => {
    daysDraftTouchedRef.current = false;
    const parsed = Number(daysDraft);
    const current = Number(resolvedDeductibleDays);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      if (resolvedDeductibleDays !== '' && resolvedDeductibleDays != null) {
        setDaysDraft(String(resolvedDeductibleDays));
      }
      toast.error('Enter deductible days in 0.5 increments');
      return;
    }
    if (Number.isFinite(current) && parsed === current) return;
    try {
      const res = await applyDeductionUpdate({ leave_total_days: parsed });
      const synced = res?.data?.leave_dates_synced;
      if (synced?.leave_start_time_period === 'PM' && startPeriod !== 'PM') {
        toast.info('Leave now starts PM so the morning session is not deducted.');
      } else if (synced?.leave_end_time_period === 'AM' && endPeriod !== 'AM') {
        toast.info('Leave now ends AM so the afternoon session is not deducted.');
      }
    } catch (error) {
      if (resolvedDeductibleDays !== '' && resolvedDeductibleDays != null) {
        setDaysDraft(String(resolvedDeductibleDays));
      }
      toast.error(error?.response?.data?.error || 'Failed to update deductible days');
    }
  }, [applyDeductionUpdate, daysDraft, resolvedDeductibleDays, startPeriod, endPeriod]);

  const handleDownloadDocument = () => {
    if (!documentBlobUrl) return;
    const ext = (mcFilePath?.split('.').pop() || 'file').toLowerCase();
    const safeName = (employeeName || 'employee').replace(/[^a-zA-Z0-9_\- ]/g, '').trim().replace(/\s+/g, '_');
    const filename = `MC_${safeName}_${employeeStaffCode || 'unknown'}${applicationId ? `_${applicationId}` : ''}.${ext}`;

    const link = document.createElement('a');
    link.href = documentBlobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Verify / Reject handlers ───────────────────────────────────────────────
  const handleValidateMc = async (approve = true) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      if (approve) {
        if (!canVerifyMc) {
          toast.error('The document, OCR data, and balance preview must be ready before verification.');
          return;
        }
        if (duplicateMcNotice) {
          toast.error(duplicateMcNotice.title);
          return;
        }
        const response = await staffAPI.verifyMcAndApproveLeave({ application_id: applicationId });
        const adjustment = response?.data?.approved_leave_overlap_adjustment;
        const successMessage = adjustment?.changed
          ? (
              `MC verified and sick leave approved successfully. `
              + `Approved existing leave was kept unchanged, and the sick leave `
              + `was adjusted from ${formatDate(adjustment.original_start_date)} `
              + `to ${formatDate(adjustment.original_end_date)} into `
              + `${formatDate(adjustment.new_start_date)} to `
              + `${formatDate(adjustment.new_end_date)}.`
            )
          : (response?.data?.message || 'MC verified successfully');
        toast.success(successMessage);
      } else {
        await staffAPI.rejectMcAndLeave({ application_id: applicationId, rejection_reason: rejectionReason });
        toast.success('MC rejected successfully');
      }

      wasFinalisedRef.current = true;
      setShowRejectForm(false);
      setRejectionReason('');
      if (onVerificationComplete) onVerificationComplete();
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.error || 'MC verification failed';
      toast.error(msg, { duration: 8000 });
      console.error('MC verify error:', e);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Cancel verified leave (soft, auditable — keeps record + MC document) ────
  const handleCancelVerifiedLeave = async () => {
    if (cancelling) return;
    const reason = cancellationReason.trim();
    if (!reason) {
      toast.error('Please enter a reason for cancelling this leave');
      return;
    }
    setCancelling(true);
    try {
      await staffAPI.cancelVerifiedMcLeave(applicationId, reason);
      toast.success('Leave cancelled and balance restored. The medical certificate is kept on record.');
      wasFinalisedRef.current = true;
      setShowCancelConfirm(false);
      setCancellationReason('');
      if (onVerificationComplete) onVerificationComplete();
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Failed to cancel leave application';
      toast.error(msg, { duration: 8000 });
      console.error('Cancel verified MC leave error:', e);
    } finally {
      setCancelling(false);
    }
  };

  // ── Close guard ────────────────────────────────────────────────────────────
  // If the verification was already finalised, close immediately.
  // Otherwise show a confirmation dialog to prevent accidental dismissal.
  const isAlreadyFinalised = ['approved', 'verified', 'rejected', 'auto_approved', 'cancelled', 'orphaned'].includes(leaveStatus);
  const canExtractMcAgain = Boolean(applicationId) && hasUploadedMc && !isAlreadyFinalised && !isOrphanedMc;

  const handleExtractMcAgain = async () => {
    if (!canExtractMcAgain || rerunningOcr) return;
    setRerunningOcr(true);
    const loadingToast = toast.loading('Extracting details…');
    try {
      const formData = new FormData();
      formData.append('application_id', applicationId);
      const resp = await api.post('/medical-certificate/trigger-ocr/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      toast.dismiss(loadingToast);
      const payload = resp?.data || {};
      if (!payload.success) {
        toast.warning(
          payload.message || payload.error || 'Could not read this certificate. Fill in the fields from the scan.',
          { duration: 6000 },
        );
        return;
      }
      const fresh = await staffAPI.getMcVerificationData(applicationId);
      if (fresh.data?.success !== false) {
        setMcOcrData(fresh.data);
      }
      if (fresh.data?.extracted_data) {
        toast.success('Extracted details updated');
      } else {
        toast.warning('Could not read this certificate. Fill in the fields from the scan.', { duration: 6000 });
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      const apiMessage =
        error?.response?.data?.message
        || error?.response?.data?.error
        || 'Failed to analyse document. Please try again.';
      toast.error(apiMessage);
    } finally {
      setRerunningOcr(false);
    }
  };

  const handleCloseRequest = useCallback(() => {
    if (wasFinalisedRef.current || isAlreadyFinalised) {
      onClose();
      return;
    }

    const hasRejectionDraft = showRejectForm && rejectionReason.trim();

    if (isHRInitiated || hasRejectionDraft) {
      setShowCloseConfirm(true);
      return;
    }

    // Field edits are already persisted on blur — nothing to lose
    onClose();
  }, [onClose, isAlreadyFinalised, isHRInitiated, showRejectForm, rejectionReason]);

  const handleConfirmedClose = useCallback(async () => {
    await safeDiscardAndClose({
      discardAction: (isHRInitiated && applicationId) ? () => staffAPI.discardMcDraft(applicationId) : undefined,
      isIgnorableError: (e) => e?.response?.status === 409,
      onBeforeClose: () => setShowCloseConfirm(false),
      onClose: () => onClose(),
      failureMessage: 'Could not fully discard this draft. Closing anyway; cleanup will be retried later.',
      logContext: 'Failed to discard HR-initiated MC draft during modal close:',
    });
  }, [isHRInitiated, applicationId, onClose, safeDiscardAndClose]);

  if (!isOpen) return null;

  return (
    <>
    <Portal>
      <div className="modal-overlay">
        <div className={`modal-container max-w-7xl ${isMobile ? 'h-[calc(100dvh-1rem)]' : 'h-[92vh] max-h-[96vh]'} flex flex-col overflow-hidden`}>
          {/* ── Header — DS: title + LeaveTypeBadge; HR context subtitle; close alone */}
          <div className="modal-header">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <h3 className="modal-title truncate min-w-0">MC Verification</h3>
                <div className="flex-shrink-0">
                  <LeaveTypeBadge leaveTypeName={displayLeaveTypeName} />
                </div>
              </div>
              {employeeName && (
                <p className="text-sm text-gray-500 mt-0.5 truncate">
                  {employeeName}
                  {employeeStaffCode && (
                    <>
                      <span className="mx-1.5 text-gray-400">·</span>
                      <span>{employeeStaffCode}</span>
                    </>
                  )}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleCloseRequest}
              className="modal-close-admin flex-shrink-0"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* ── Content (split-screen) — each panel scrolls independently ───── */}
          <div className="modal-content !overflow-hidden !p-0 flex flex-col">
            <div className={`flex-1 flex ${isMobile ? 'flex-col' : 'flex-row'} overflow-hidden min-h-0`}>
              {/* Mobile tab bar */}
              {isMobile && (
                <MobileVerificationTabs
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  documentLabel="Document"
                  formLabel="Verification"
                  isAdmin={true}
                />
              )}
              {/* Left Panel — Document Preview */}
              <div className={`${isMobile ? (activeTab === 'document' ? 'flex-1' : 'hidden') : 'w-2/3'} ${!isMobile ? 'border-r' : ''} border-gray-200 flex flex-col min-h-0`}>
                {/* Desktop only — mobile tabs already label the Document surface */}
                {!isMobile && (
                  <VerificationPanelHeader title="Medical Certificate" />
                )}
                <div className="flex-1 p-4 overflow-hidden min-h-0">
                  {loadingMcData ? (
                    <div className="flex items-center justify-center h-full">
                      <LoadingSpinner size="lg" />
                    </div>
                  ) : isDocumentUnavailable ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="max-w-md text-center">
                        <AlertTriangle className="w-14 h-14 mx-auto mb-4 text-red-500" />
                        <h4 className="text-base font-semibold text-red-700 mb-2">
                          Medical Certificate Unavailable
                        </h4>
                        <p className="text-sm text-gray-600">
                          {documentIssue.message}
                        </p>
                      </div>
                    </div>
                  ) : mcFilePath ? (
                    <div className="relative w-full h-full" style={isMobile ? undefined : { minHeight: '600px' }}>
                      {loadingDocument ? (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          <div className="text-center">
                            <LoadingSpinner size="lg" />
                            <p className="mt-2">Loading document...</p>
                          </div>
                        </div>
                      ) : documentBlobUrl && getDocumentType() === 'image' ? (
                        <div
                          ref={viewer.containerRef}
                          className="relative w-full h-full overflow-hidden bg-white border rounded-lg flex items-center justify-center"
                          {...viewer.panHandlers}
                          style={viewer.containerStyle}
                        >
                          <img
                            src={documentBlobUrl}
                            alt="Medical Certificate"
                            className="select-none pointer-events-none"
                            draggable={false}
                            style={{
                              transform: viewer.imageTransform,
                              transformOrigin: 'center center',
                              maxWidth: '100%',
                              maxHeight: '100%',
                              transition: viewer.isPanning ? 'none' : 'transform 0.15s ease',
                            }}
                          />
                          {/* Floating zoom controls — image viewer only */}
                          <div
                            className="absolute bottom-3 right-3 z-10 flex items-center gap-0.5 bg-white/95 border border-gray-200 rounded-full shadow-md p-1"
                            onMouseDown={e => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={viewer.zoomOut}
                              className="icon-button-admin"
                              title="Zoom out"
                              aria-label="Zoom out"
                            >
                              <ZoomOut className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={viewer.zoomIn}
                              className="icon-button-admin"
                              title="Zoom in"
                              aria-label="Zoom in"
                            >
                              <ZoomIn className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={viewer.rotate}
                              className="icon-button-admin"
                              title="Rotate clockwise"
                              aria-label="Rotate clockwise"
                            >
                              <RotateCw className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={viewer.reset}
                              className="icon-button-admin"
                              title="Fit to panel"
                              aria-label="Fit to panel"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                            <div className="w-px h-6 bg-gray-200 mx-0.5" />
                            <button
                              type="button"
                              onClick={handleDownloadDocument}
                              className="icon-button-admin"
                              title="Download"
                              aria-label="Download medical certificate"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : documentBlobUrl && getDocumentType() === 'pdf' ? (
                        <div className="relative w-full h-full border rounded-lg bg-white overflow-hidden" style={isMobile ? undefined : { minHeight: '600px' }}>
                          <PdfViewer
                            src={documentBlobUrl}
                            title="Medical Certificate"
                            fileName={`MC_${employeeStaffCode || 'document'}.pdf`}
                            onDownload={handleDownloadDocument}
                          />
                        </div>
                      ) : documentBlobUrl && getDocumentType() !== 'image' ? (
                        <div className="relative w-full h-full border rounded-lg bg-white overflow-hidden" style={isMobile ? undefined : { minHeight: '600px' }}>
                          <iframe
                            key={documentBlobUrl}
                            src={documentBlobUrl}
                            className="w-full h-full border-0"
                            title="Medical Certificate"
                            style={isMobile ? { height: '100%', width: '100%', display: 'block' } : { minHeight: '600px', height: '100%', width: '100%', display: 'block' }}
                            allow="fullscreen"
                          />
                        </div>
                      ) : documentError ? (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          <div className="text-center">
                            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500" />
                            <p className="text-red-600 font-medium mb-2">Failed to load document</p>
                            <p className="text-sm text-gray-500 mb-4">{documentError}</p>
                            <button
                              onClick={() => loadDocumentBlob(mcFilePath)}
                              className="btn btn-admin-outline"
                            >
                              Retry Loading
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          <LoadingSpinner size="lg" />
                        </div>
                      )}
                    </div>
                  ) : extractedData ? (
                    // OCR data exists but file path is missing
                    <div className="flex items-center justify-center h-full text-orange-500">
                      <div className="text-center">
                        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-orange-400" />
                        <p className="font-medium text-orange-600 mb-2">Medical Certificate File Not Found</p>
                        <p className="text-sm mt-2 mb-4">OCR data exists but the original file cannot be located.</p>
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                          <p className="text-sm text-orange-800 font-medium mb-2">Possible Actions:</p>
                          <ul className="text-sm text-orange-700 space-y-1">
                            <li>• File may have been moved or deleted</li>
                            <li>• Check server logs for file path issues</li>
                            <li>• Employee may need to re-upload the MC</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  ) : (hasUploadedMc) ? (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <div className="flex items-center justify-center mb-3">
                          <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
                            <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                            OCR Pending
                          </span>
                        </div>
                        <p className="font-medium text-gray-700 mb-1">OCR processing…</p>
                        <p className="text-sm text-gray-500">Extracting information from the MC. You can proceed with manual review or refresh once processed.</p>
                      </div>
                    </div>
                  ) : (
                    // MC not uploaded at all
                    <div className="flex items-center justify-center h-full">
                      <div className="max-w-md text-center">
                        <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-orange-400" />
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">Medical Certificate Required</h4>
                        <p className="text-gray-600 mb-4">
                          This leave application requires a medical certificate, but none has been uploaded yet.
                        </p>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                          <h5 className="font-medium text-blue-900 mb-2">Application Summary</h5>
                          <div className="space-y-2 text-sm text-left">
                            <div className="flex justify-between">
                              <span className="text-blue-700">Employee:</span>
                              <span className="text-blue-900 font-medium">{employeeName}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-blue-700">Leave Type:</span>
                              <LeaveTypeBadge leaveTypeName={displayLeaveTypeName} size="xs" />
                            </div>
                            {displayStartDate && (
                              <div className="flex justify-between">
                                <span className="text-blue-700">Period:</span>
                                <span className="text-blue-900 font-medium">
                                  {formatDate(displayStartDate)} - {formatDate(displayEndDate)}
                                </span>
                              </div>
                            )}
                            {displayTotalDays && (
                              <div className="flex justify-between">
                                <span className="text-blue-700">Total Days:</span>
                                <span className="text-blue-900 font-medium">{displayTotalDays} {pluralizeDays(displayTotalDays)}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h5 className="font-medium text-yellow-900 mb-2">HR Verification Options</h5>
                          <p className="text-sm text-yellow-800 mb-3">
                            As the required medical certificate is missing, you have the following options:
                          </p>
                          <div className="space-y-2 text-sm text-left text-yellow-800">
                            <div className="flex items-start">
                              <span className="text-yellow-600 mr-2">•</span>
                              <span>Reject the application and request the employee to upload the MC</span>
                            </div>
                            <div className="flex items-start">
                              <span className="text-yellow-600 mr-2">•</span>
                              <span>Contact the employee to provide the missing documentation</span>
                            </div>
                            <div className="flex items-start">
                              <span className="text-yellow-600 mr-2">•</span>
                              <span>Approve only if you have alternative justification</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Panel — OCR Extracted Fields + Employee Verification */}
              <div className={`${isMobile ? (activeTab === 'form' ? 'flex-1' : 'hidden') : 'w-1/3'} flex flex-col min-h-0 min-w-0 overflow-hidden`}>
                {/* Desktop only — mobile tabs already label Verification; keep confidence chip on desktop */}
                {!isMobile && (
                  <VerificationPanelHeader
                    title="Extracted Information"
                    trailing={extractedData?.ocr_confidence_score ? (
                      <InfoBadge color="green" label={`${extractedData.ocr_confidence_score}% Confidence`} />
                    ) : null}
                  />
                )}
                <div className="flex-1 p-4 overflow-y-auto overflow-x-hidden overscroll-y-contain min-h-0" style={{ touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }}>
                  {canExtractMcAgain && extractedData ? (
                    <div className="flex justify-end mb-4">
                      <ExtractAgainButton onClick={handleExtractMcAgain} busy={rerunningOcr} />
                    </div>
                  ) : null}
                  {canExtractMcAgain && !extractedData && !loadingMcData ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        <span className="font-semibold text-amber-800 text-sm">No extracted data found</span>
                      </div>
                      <p className="text-xs text-amber-700 mb-3">
                        We couldn&apos;t read the details from this certificate. Fill in the fields from the scan, or extract them again.
                      </p>
                      <ExtractAgainButton onClick={handleExtractMcAgain} busy={rerunningOcr} />
                    </div>
                  ) : null}
                  {loadingMcData ? (
                    <div className="flex items-center justify-center h-full">
                      <LoadingSpinner size="lg" />
                    </div>
                  ) : isOrphanedMc ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="max-w-sm text-center">
                        <AlertTriangle className="w-14 h-14 mx-auto mb-4 text-orange-400" />
                        <h4 className="text-base font-semibold text-gray-900 mb-2">Abandoned Document</h4>
                        <p className="text-sm text-gray-600 mb-4">
                          The leave application this medical certificate was submitted against no longer exists.
                        </p>
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-left space-y-2">
                          <p className="text-sm font-medium text-orange-800">Document details</p>
                          <div className="flex justify-between text-sm">
                            <span className="text-orange-700">Staff ID</span>
                            <span className="font-mono font-medium text-orange-900">{employeeStaffCode || employeeName}</span>
                          </div>
                          {employeeName && employeeStaffCode && (
                            <div className="flex justify-between text-sm">
                              <span className="text-orange-700">Name</span>
                              <span className="font-medium text-orange-900">{employeeName}</span>
                            </div>
                          )}
                          {mcOcrData?.document_approval_id && (
                            <div className="flex justify-between text-sm">
                              <span className="text-orange-700">Document ID</span>
                              <span className="font-mono font-medium text-orange-900">#{mcOcrData.document_approval_id}</span>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-4">
                          Dismiss this document to remove it from the pending verification queue.
                        </p>
                      </div>
                    </div>
                  ) : hasBlockingDocumentIssue ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="max-w-sm text-center">
                        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-red-500" />
                        <h4 className="text-base font-semibold text-red-700 mb-2">
                          Verification Blocked
                        </h4>
                        <p className="text-sm text-gray-600">
                          {documentIssue.message}
                        </p>
                      </div>
                    </div>
                  ) : extractedData ? (
                    <form className="space-y-4">
                      {/* Leave Type Banner — always visible so admin knows which balance is affected */}
                      <div className={`rounded-xl p-4 border-2 ${
                        isHospitalisationLeave
                          ? 'bg-red-50 border-red-300'
                          : 'bg-amber-50 border-amber-300'
                      }`}>
                        <div className="flex items-center gap-2.5">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                            isHospitalisationLeave ? 'bg-red-100' : 'bg-amber-100'
                          }`}>
                            <LeaveTypeIcon className={`w-5 h-5 ${
                              isHospitalisationLeave ? 'text-red-700' : 'text-amber-700'
                            }`} />
                          </div>
                          <div>
                            <p className={`text-sm font-semibold ${
                              isHospitalisationLeave ? 'text-red-900' : 'text-amber-900'
                            }`}>{displayLeaveTypeName}</p>
                            <p className={`text-xs ${
                              isHospitalisationLeave ? 'text-red-700' : 'text-amber-700'
                            }`}>
                              {!isSickLeaveType
                                ? 'This MC is linked to a non-sick leave application — verify the correct leave type before approving.'
                                : hasBookedMixedAllocation
                                  ? `Recorded as ${bookedAllocationSummary}`
                                  : unpaidInfo?.isFullyUnpaid
                                  ? 'Paid balance exhausted — recorded as Unpaid Leave'
                                  : unpaidInfo
                                    ? `Partly unpaid — ${formatLeaveDays(unpaidInfo.paidDays)} paid + ${formatLeaveDays(unpaidInfo.unpaidDays)} Unpaid Leave`
                                    : `Deducting from ${isHospitalisationLeave ? 'Hospitalisation' : 'Outpatient Sick'} Leave balance`}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Patient Information */}
                      <div className="bg-white rounded-xl shadow-sm border p-4">
                        <h5 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <User className="w-4 h-4 text-blue-600" />
                          Patient Information
                        </h5>
                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <label className="form-label">NRIC/FIN</label>
                            <input
                              type="text"
                              defaultValue={extractedData.ocr_patient_nric_fin || ''}
                              className="input-admin font-mono"
                              placeholder="Enter NRIC/FIN"
                              onBlur={(e) => staffAPI.updateMcOcrField({ application_id: applicationId, field_name: 'ocr_patient_nric_fin', new_value: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="form-label">Full Name</label>
                            <input
                              type="text"
                              defaultValue={extractedData.ocr_patient_full_name || ''}
                              className="input-admin"
                              placeholder="Enter full name"
                              onBlur={(e) => staffAPI.updateMcOcrField({ application_id: applicationId, field_name: 'ocr_patient_full_name', new_value: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>

                      {/* MC Details */}
                      <div className="bg-white rounded-xl shadow-sm border p-4">
                        <h5 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4 text-green-600" />
                          Medical Certificate Details
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="form-label">MC Number</label>
                            <input
                              type="text"
                              defaultValue={extractedData.ocr_mc_number || ''}
                              className="input-admin font-mono"
                              placeholder="Enter MC number"
                              onBlur={(e) => staffAPI.updateMcOcrField({ application_id: applicationId, field_name: 'ocr_mc_number', new_value: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="form-label">Issue Date</label>
                            <DatePickerField
                              theme="admin"
                              selected={parseLocalDate(extractedData.ocr_mc_issue_date)}
                              onChange={(date) => {
                                const val = date ? toLocalISODate(date) : '';
                                setMcOcrData(prev => ({
                                  ...prev,
                                  extracted_data: { ...prev?.extracted_data, ocr_mc_issue_date: val }
                                }));
                                staffAPI.updateMcOcrField({ application_id: applicationId, field_name: 'ocr_mc_issue_date', new_value: val });
                              }}
                              placeholderText="Select date"
                            />
                          </div>
                          <div>
                            <label className="form-label">Start Date</label>
                            <div className={isMobile ? 'flex flex-nowrap items-center gap-2 min-w-0' : 'flex flex-col gap-2'}>
                              <div className={isMobile ? 'flex-1 min-w-0' : 'w-full min-w-0'}>
                                <DatePickerField
                                  theme="admin"
                                  selected={parseLocalDate(extractedData.ocr_mc_start_date)}
                                  onChange={async (date) => {
                                    const val = date ? toLocalISODate(date) : '';
                                    setMcOcrData(prev => ({
                                      ...prev,
                                      extracted_data: { ...prev?.extracted_data, ocr_mc_start_date: val }
                                    }));
                                    const res = await staffAPI.updateMcOcrField({ application_id: applicationId, field_name: 'ocr_mc_start_date', new_value: val });
                                    mergeDeductionSync(res);
                                  }}
                                  placeholderText="Select date"
                                  disabled={!canEditDeduction}
                                />
                              </div>
                              <LeaveHalfDaySegmentedControl
                                theme="admin"
                                stretch={!isMobile}
                                value={startPeriod}
                                onChange={(period) => handleSessionChange('start', period)}
                                ariaLabel="First day: morning or afternoon"
                                className={!canEditDeduction ? 'pointer-events-none opacity-60' : ''}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="form-label">End Date</label>
                            <div className={isMobile ? 'flex flex-nowrap items-center gap-2 min-w-0' : 'flex flex-col gap-2'}>
                              <div className={isMobile ? 'flex-1 min-w-0' : 'w-full min-w-0'}>
                                <DatePickerField
                                  theme="admin"
                                  selected={parseLocalDate(
                                    extractedData.ocr_mc_end_date
                                    || (() => {
                                      const days = parseInt(extractedData.ocr_mc_total_days, 10);
                                      if (extractedData.ocr_mc_start_date && days > 0) {
                                        const s = new Date(extractedData.ocr_mc_start_date + 'T00:00:00');
                                        if (!isNaN(s)) {
                                          s.setDate(s.getDate() + days - 1);
                                          return toLocalISODate(s);
                                        }
                                      }
                                      return '';
                                    })()
                                  )}
                                  onChange={async (date) => {
                                    const val = date ? toLocalISODate(date) : '';
                                    setMcOcrData(prev => ({
                                      ...prev,
                                      extracted_data: { ...prev?.extracted_data, ocr_mc_end_date: val }
                                    }));
                                    const res = await staffAPI.updateMcOcrField({ application_id: applicationId, field_name: 'ocr_mc_end_date', new_value: val });
                                    mergeDeductionSync(res);
                                  }}
                                  placeholderText="Select date"
                                  disabled={!canEditDeduction}
                                />
                              </div>
                              <LeaveHalfDaySegmentedControl
                                theme="admin"
                                stretch={!isMobile}
                                value={endPeriod}
                                onChange={(period) => handleSessionChange('end', period)}
                                ariaLabel="Last day: morning or afternoon"
                                className={!canEditDeduction ? 'pointer-events-none opacity-60' : ''}
                              />
                            </div>
                          </div>
                          <div className="md:col-span-2">
                            <label className="form-label flex items-center gap-2">
                              Total Days (deductible)
                              {isSessionAdjusted && (
                                <InfoBadge color="yellow" label="Adjusted" />
                              )}
                            </label>
                            {canEditDeduction ? (
                              <div className="flex items-center gap-2 min-w-0">
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  step="0.5"
                                  min="0.5"
                                  value={daysDraft}
                                  onChange={(e) => {
                                    daysDraftTouchedRef.current = true;
                                    setDaysDraft(e.target.value);
                                  }}
                                  onBlur={handleDaysCommit}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      e.currentTarget.blur();
                                    }
                                  }}
                                  onWheel={(e) => e.currentTarget.blur()}
                                  className="input-admin w-28"
                                  aria-label="Deductible working days"
                                />
                                <span className="text-sm text-gray-600 whitespace-nowrap">
                                  work {pluralizeDays(Number(daysDraft) || resolvedDeductibleDays)}
                                </span>
                              </div>
                            ) : (
                              <div className="field-display">
                                <span className="text-sm text-gray-900 font-medium flex-1">
                                  {(() => {
                                    const days = resolvedDeductibleDays;
                                    if (days === '' || days == null) {
                                      return extractedData ? 'Calculating…' : '—';
                                    }
                                    const n = Number(days);
                                    return `${n} work ${pluralizeDays(n)}`;
                                  })()}
                                </span>
                              </div>
                            )}
                            {isSessionAdjusted && fullDayDeductible != null && (
                              <p className="mt-1 text-xs text-gray-500">
                                Certificate window is {Number(fullDayDeductible)} work {pluralizeDays(fullDayDeductible)}.
                                {startPeriod === 'PM' && endPeriod === 'PM' && displayStartDate && (
                                  <> Deducting from PM on {formatDate(displayStartDate)} because the employee attended the morning.</>
                                )}
                                {startPeriod === 'AM' && endPeriod === 'AM' && displayEndDate && (
                                  <> Leave ends AM on {formatDate(displayEndDate)} — afternoon is not deducted.</>
                                )}
                                {startPeriod === 'PM' && endPeriod === 'AM' && displayStartDate && displayEndDate && (
                                  <> Deducting PM on {formatDate(displayStartDate)} through AM on {formatDate(displayEndDate)}.</>
                                )}
                              </p>
                            )}
                            {!isSessionAdjusted && canEditDeduction && (
                              <p className="mt-1 text-xs text-gray-500">
                                Certificate days stay as extracted. Change AM/PM if the employee worked a session, or type the deductible days.
                              </p>
                            )}
                            {ocrStatedDays != null
                              && displayTotalDays !== ''
                              && Number(ocrStatedDays) !== Number(displayTotalDays) && (
                              <p className="mt-1 text-xs text-gray-500">
                                Doctor stated {Number(ocrStatedDays)} calendar {pluralizeDays(ocrStatedDays)};
                                {' '}{excludedDates.length} excluded (rest day / public holiday).
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Medical Information */}
                      <div className="bg-white rounded-xl shadow-sm border p-4">
                        <h5 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <Stethoscope className="w-4 h-4 text-purple-600" />
                          Medical Information
                        </h5>
                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <label className="form-label">Clinic/Hospital</label>
                            <input
                              type="text"
                              defaultValue={extractedData.ocr_clinic_hospital_name || ''}
                              className="input-admin"
                              placeholder="Enter clinic/hospital name"
                              onBlur={(e) => staffAPI.updateMcOcrField({ application_id: applicationId, field_name: 'ocr_clinic_hospital_name', new_value: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="form-label">Doctor Name</label>
                            <input
                              type="text"
                              defaultValue={extractedData.ocr_doctor_name || ''}
                              className="input-admin"
                              placeholder="Enter doctor name"
                              onBlur={(e) => staffAPI.updateMcOcrField({ application_id: applicationId, field_name: 'ocr_doctor_name', new_value: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="form-label">Diagnosis</label>
                            <input
                              type="text"
                              defaultValue={extractedData.ocr_diagnosis || ''}
                              className="input-admin"
                              placeholder="Enter diagnosis"
                              onBlur={(e) => staffAPI.updateMcOcrField({ application_id: applicationId, field_name: 'ocr_diagnosis', new_value: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Employee Verification with NRIC and Name matching */}
                      <div className="bg-white rounded-xl shadow-sm border p-4">
                        <h5 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <Shield className="w-4 h-4 text-orange-600" />
                          Employee Verification
                          {(() => {
                            const nricMatch = employeeData.nric_fin && extractedData.ocr_patient_nric_fin &&
                              employeeData.nric_fin.toLowerCase().trim() === extractedData.ocr_patient_nric_fin.toLowerCase().trim();
                            const nameMatch = nameTokensMatch(employeeData.full_name, extractedData.ocr_patient_full_name);
                            if (nricMatch && nameMatch) {
                              return <InfoBadge color="green" label="Fully Verified" className="ml-2" />;
                            } else if (nricMatch) {
                              // NRIC is the authoritative check — name discrepancy is likely OCR noise or name order
                              return <InfoBadge color="yellow" label="NRIC Verified" className="ml-2" />;
                            } else if (nameMatch) {
                              return <InfoBadge color="yellow" label="Name Match Only" className="ml-2" />;
                            }
                            return <InfoBadge color="red" label="Verification Required" className="ml-2" />;
                          })()}
                        </h5>
                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <label className="form-label">Employee NRIC/FIN</label>
                            <div className="field-display">
                              <span className="text-sm text-gray-900 font-mono flex-1">{employeeData.nric_fin || '—'}</span>
                              {(() => {
                                const nricMatch = employeeData.nric_fin && extractedData.ocr_patient_nric_fin &&
                                  employeeData.nric_fin.toLowerCase().trim() === extractedData.ocr_patient_nric_fin.toLowerCase().trim();
                                return nricMatch
                                  ? <CheckCircle className="w-4 h-4 text-green-600" />
                                  : <AlertTriangle className="w-4 h-4 text-red-600" />;
                              })()}
                            </div>
                          </div>
                          <div>
                            <label className="form-label">Employee Name</label>
                            <div className="field-display">
                              <span className="text-sm text-gray-900 flex-1">{employeeData.full_name || '—'}</span>
                              {(() => {
                                const nameMatch = nameTokensMatch(employeeData.full_name, extractedData.ocr_patient_full_name);
                                return nameMatch
                                  ? <CheckCircle className="w-4 h-4 text-green-600" />
                                  : <AlertTriangle className="w-4 h-4 text-yellow-600" />;
                              })()}
                            </div>
                          </div>
                          <div>
                            <label className="form-label">
                              {isSessionAdjusted ? 'Effective Leave Period' : 'MC Stated Period'}
                            </label>
                            <div className="field-display">
                              {(() => {
                                const start = employeeData.leave_start_date || displayStartDate;
                                const end = employeeData.leave_end_date || displayEndDate;
                                if (!start) {
                                  return <span className="text-sm text-gray-900 flex-1">—</span>;
                                }
                                return (
                                  <LeavePeriodDisplay
                                    startDate={start}
                                    endDate={end || start}
                                    startTimePeriod={startPeriod}
                                    endTimePeriod={endPeriod}
                                    totalDays={resolvedDeductibleDays}
                                    showIcon={false}
                                    size="sm"
                                    className="flex-1"
                                  />
                                );
                              })()}
                            </div>
                          </div>
                          {effectiveCoverageDays && (
                            <div>
                              <div className="flex items-center justify-between bg-blue-50 rounded-lg p-3">
                                <span className="text-sm font-medium text-gray-700">Effective Coverage</span>
                                <span className="text-base font-semibold text-gray-900">
                                  {formatLeaveDays(effectiveCoverageDays)} {pluralizeDays(effectiveCoverageDays)}
                                </span>
                              </div>
                            </div>
                          )}
                          {sickLeaveOverlap && sickLeaveOverlap.length > 0 && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-1.5">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                                <div className="text-xs text-orange-800 space-y-1">
                                  {sickLeaveOverlap.map((ov, idx) => (
                                    <div key={idx}>
                                      <span className="font-semibold">{ov.overlap_days} day overlap</span> with approved {ov.leave_type} ({formatDate(ov.start_date)} – {formatDate(ov.end_date)}).
                                      {' '}{effectiveDateAdjustment?.changed
                                        ? <>Verification will automatically commence this application on <strong>{formatDate(effectiveDateAdjustment.new_start_date)}</strong>, with no double deduction.</>
                                        : <>Adjust the start date to <strong>{formatDate(ov.suggested_start_date)}</strong> to avoid double deduction.</>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                          {duplicateMcNotice && (
                            <DuplicateMcNotice notice={duplicateMcNotice} />
                          )}
                          {effectiveDateAdjustment?.changed && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <div className="flex items-start gap-2">
                                <Shield className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                                <div className="text-xs text-blue-800 space-y-1">
                                  <p className="font-semibold">Effective leave period adjusted automatically</p>
                                  <p>
                                    Existing approved leave remains unchanged. This application will commence on{' '}
                                    <strong>{formatDate(effectiveDateAdjustment.new_start_date)}</strong> and run through{' '}
                                    <strong>{formatDate(effectiveDateAdjustment.new_end_date)}</strong>, with no duplicate deduction.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </form>
                  ) : (hasUploadedMc) ? (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <Eye className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                        <p>OCR processing...</p>
                        <p className="text-sm mt-2">Extracting information from MC</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      <div className="text-center">
                        <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">No MC data available</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Sticky Footer (Verify / Reject actions) ─────────────────── */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex-shrink-0">
                  {isOrphanedMc ? (
                    <>
                      {showRejectForm ? (
                        <div className="space-y-2">
                          <label className="block text-xs font-medium text-gray-700">Reason for dismissal <span className="text-gray-400">(optional)</span></label>
                          <textarea
                            rows={2}
                            className="textarea-admin text-sm"
                            placeholder="Enter reason for dismissing this abandoned document..."
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                          />
                          <div className="flex gap-3 btn-row-spread">
                            <button
                              onClick={() => { setShowRejectForm(false); setRejectionReason(''); }}
                              disabled={submitting}
                              className="btn btn-outline flex-1"
                            >
                              Back
                            </button>
                            <button
                              onClick={async () => {
                                if (submitting) return;
                                setSubmitting(true);
                                try {
                                  await staffAPI.rejectMcAndLeave({
                                    application_id: applicationId,
                                    document_approval_id: mcOcrData?.document_approval_id,
                                    rejection_reason: rejectionReason.trim() || 'Abandoned MC dismissed by HR',
                                  });
                                  toast.success('Abandoned document dismissed');
                                  wasFinalisedRef.current = true;
                                  setShowRejectForm(false);
                                  setRejectionReason('');
                                  if (onVerificationComplete) onVerificationComplete();
                                  onClose();
                                } catch (e) {
                                  const msg = e?.response?.data?.error || 'Failed to dismiss document';
                                  toast.error(msg, { duration: 8000 });
                                } finally {
                                  setSubmitting(false);
                                }
                              }}
                              disabled={submitting}
                              className="btn btn-danger flex-1"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              {submitting ? 'Dismissing...' : 'Confirm Dismiss'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center justify-center text-orange-600 text-sm font-medium mb-1">
                            <AlertTriangle className="w-4 h-4 mr-1.5" />
                            Abandoned — linked leave application was deleted
                          </div>
                          <button
                            onClick={() => setShowRejectForm(true)}
                            disabled={submitting}
                            className="btn btn-danger w-full"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Dismiss Document
                          </button>
                        </div>
                      )}
                    </>
                  ) : (hasUploadedMc || mcFilePath) ? (
                    <>
                      {leaveStatus === 'submitted' || leaveStatus === 'draft' ? (
                        !extractedData && !hasBlockingDocumentIssue ? (
                          <div className="flex items-center justify-center text-yellow-600">
                            <Clock className="w-4 h-4 mr-1 animate-spin" />
                            <span className="text-sm font-medium">Awaiting OCR data…</span>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {/* Exact approval allocation from the backend source of truth. */}
                            {duplicateMcNotice ? null : hasBlockingDocumentIssue ? (
                              <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs bg-red-50 text-red-800 border border-red-200">
                                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span>{documentIssue.message}</span>
                              </div>
                            ) : !consumptionPreview ? (
                              <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                                <Clock className="w-3.5 h-3.5 flex-shrink-0 animate-spin" />
                                <span>Calculating the effective leave period and balance allocation…</span>
                              </div>
                            ) : hasMixedAllocation ? (
                              <div className="flex flex-col gap-1.5 px-3 py-2.5 rounded-lg text-xs bg-blue-50 text-blue-900 border border-blue-200">
                                <div className="flex items-start gap-2">
                                  <Wallet className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
                                  <div className="min-w-0 space-y-1">
                                    <p className="font-semibold">
                                      {consumptionPreview.paid_days === 0
                                        ? <>No paid {displayLeaveTypeName} balance remaining.</>
                                        : <>Only {formatLeaveDays(consumptionPreview.paid_days)} paid {displayLeaveTypeName} {pluralizeDays(consumptionPreview.paid_days)} remaining.</>}
                                    </p>
                                    <p className="font-normal">
                                      Verifying will book{' '}
                                      {consumptionPreview.paid_days > 0 && (
                                        <><strong>{formatLeaveDays(consumptionPreview.paid_days)} {pluralizeDays(consumptionPreview.paid_days)}</strong> paid {displayLeaveTypeName}</>
                                      )}
                                      {consumptionPreview.annual_leave_days > 0 && (
                                        <> + <strong>{formatLeaveDays(consumptionPreview.annual_leave_days)} {pluralizeDays(consumptionPreview.annual_leave_days)}</strong> Annual Leave</>
                                      )}
                                      {consumptionPreview.unpaid_days > 0 && (
                                        <> + <strong>{formatLeaveDays(consumptionPreview.unpaid_days)} {pluralizeDays(consumptionPreview.unpaid_days)}</strong> Unpaid Leave</>
                                      )}.
                                    </p>
                                    <p className="font-normal text-blue-700">
                                      Rest days and public holidays remain covered by the MC without consuming Annual Leave.
                                    </p>
                                  </div>
                                </div>
                                {excludedDates.length > 0 && (
                                  <span className="pl-6 font-normal text-blue-700">
                                    {excludedDates.length} of {deductionBreakdown.calendar_days} calendar {pluralizeDays(deductionBreakdown.calendar_days)} excluded
                                    {' ('}
                                    {excludedDates.map((d, i) => (
                                      <span key={d.date}>
                                        {i > 0 ? ', ' : ''}
                                        {formatDate(d.date)} – {d.reason}
                                      </span>
                                    ))}
                                    {')'}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className={`flex flex-col gap-1 px-3 py-2 rounded-lg text-xs font-medium ${
                                isHospitalisationLeave
                                  ? 'bg-red-50 text-red-800 border border-red-200'
                                  : 'bg-amber-50 text-amber-800 border border-amber-200'
                              }`}>
                                <div className="flex items-center gap-2">
                                  <LeaveTypeIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                  <span>
                                    Verifying will deduct {(displayTotalDays !== '' && displayTotalDays != null) ? `${Number(displayTotalDays)} ${pluralizeDays(displayTotalDays)}` : 'days'} from <strong>{displayLeaveTypeName}</strong>
                                  </span>
                                </div>
                                {excludedDates.length > 0 && (
                                  <span className="pl-5 font-normal">
                                    {excludedDates.length} of {deductionBreakdown.calendar_days} calendar {pluralizeDays(deductionBreakdown.calendar_days)} excluded
                                    {' ('}
                                    {excludedDates.map((d, i) => (
                                      <span key={d.date}>
                                        {i > 0 ? ', ' : ''}
                                        {formatDate(d.date)} – {d.reason}
                                      </span>
                                    ))}
                                    {')'}
                                  </span>
                                )}
                              </div>
                            )}
                            {showRejectForm ? (
                              <div className="space-y-2">
                                <label className="block text-xs font-medium text-gray-700">Reason for rejection</label>
                                <textarea
                                  rows={3}
                                  className="textarea-admin text-sm"
                                  placeholder="Enter reason for MC rejection..."
                                  value={rejectionReason}
                                  onChange={(e) => setRejectionReason(e.target.value)}
                                />
                                <div className="flex gap-3 btn-row-spread">
                                  <button
                                    onClick={() => { setShowRejectForm(false); setRejectionReason(''); }}
                                    disabled={submitting}
                                    className="btn btn-outline flex-1"
                                  >
                                    Back
                                  </button>
                                  <button
                                    onClick={() => handleValidateMc(false)}
                                    disabled={submitting || !rejectionReason.trim()}
                                    className="btn btn-danger flex-1"
                                  >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    {submitting ? 'Processing...' : 'Confirm Reject'}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex gap-3 btn-row-spread">
                                <button
                                  onClick={() => setShowRejectForm(true)}
                                  disabled={submitting}
                                  className="btn btn-danger flex-1"
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  Reject MC
                                </button>
                                <button
                                  onClick={() => handleValidateMc(true)}
                                  disabled={submitting || !!duplicateMcNumber || !canVerifyMc}
                                  title={
                                    duplicateMcNotice?.tone === 'error'
                                      ? 'This certificate is already used on another application. Verifying this one would use another sick day.'
                                      : duplicateMcNotice
                                        ? 'This certificate is also on another application that is not approved yet. Verify only one.'
                                        : !canVerifyMc
                                          ? 'The document, OCR data, and balance preview must be ready before verification'
                                          : undefined
                                  }
                                  className="btn btn-success flex-1"
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  {submitting
                                    ? 'Processing...'
                                    : hasBlockingDocumentIssue
                                      ? 'Verify Unavailable'
                                      : 'Verify MC'}
                                </button>
                              </div>
                            )}
                          </div>
                        )
                      ) : (isSickLeaveType && leaveStatus === 'approved') || leaveStatus === 'verified' ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-center text-green-600">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            <span className="text-sm font-medium">MC Verified &amp; Leave Granted</span>
                          </div>
                          {hasBookedMixedAllocation ? (
                            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs bg-blue-50 text-blue-900 border border-blue-200">
                              <Wallet className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
                              <span>
                                Recorded as{' '}
                                {bookedSickDays > 0 && (
                                  <><strong>{formatLeaveDays(bookedSickDays)} {pluralizeDays(bookedSickDays)}</strong> paid {displayLeaveTypeName}</>
                                )}
                                {bookedAnnualDays > 0 && (
                                  <>{bookedSickDays > 0 ? ' + ' : ''}<strong>{formatLeaveDays(bookedAnnualDays)} {pluralizeDays(bookedAnnualDays)}</strong> Annual Leave</>
                                )}
                                {bookedUnpaidDays > 0 && (
                                  <>{bookedSickDays > 0 || bookedAnnualDays > 0 ? ' + ' : ''}<strong>{formatLeaveDays(bookedUnpaidDays)} {pluralizeDays(bookedUnpaidDays)}</strong> Unpaid Leave</>
                                )}.
                              </span>
                            </div>
                          ) : unpaidInfo && (
                            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs bg-blue-50 text-blue-900 border border-blue-200">
                              <Wallet className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
                              <span>
                                {unpaidInfo.isFullyUnpaid ? (
                                  <>Paid {displayLeaveTypeName} was exhausted — this leave is recorded as <strong>{formatLeaveDays(unpaidInfo.unpaidDays)} {pluralizeDays(unpaidInfo.unpaidDays)} Unpaid Leave</strong>.</>
                                ) : (
                                  <>Recorded as <strong>{formatLeaveDays(unpaidInfo.paidDays)} {pluralizeDays(unpaidInfo.paidDays)}</strong> paid {displayLeaveTypeName} + <strong>{formatLeaveDays(unpaidInfo.unpaidDays)} {pluralizeDays(unpaidInfo.unpaidDays)}</strong> Unpaid Leave.</>
                                )}
                              </span>
                            </div>
                          )}
                          <div className="flex gap-3 btn-row-spread">
                            <button onClick={onClose} className="btn btn-outline flex-1">
                              <X className="w-4 h-4 mr-2" />
                              Close
                            </button>
                            <button
                              onClick={() => { setCancellationReason(''); setShowCancelConfirm(true); }}
                              disabled={cancelling}
                              className="btn btn-danger flex-1"
                              title="Cancel this leave and restore the balance (the medical certificate is kept on record)"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Cancel Leave
                            </button>
                          </div>
                        </div>
                      ) : leaveStatus === 'cancelled' ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-center text-gray-600">
                            <XCircle className="w-4 h-4 mr-1" />
                            <span className="text-sm font-medium">Leave Cancelled</span>
                          </div>
                          <p className="text-sm text-gray-600 text-center">
                            This application was withdrawn. The medical certificate is kept on record and does not need verification.
                          </p>
                          <button onClick={onClose} className="btn btn-outline w-full">
                            <X className="w-4 h-4 mr-2" />
                            Close
                          </button>
                        </div>
                      ) : leaveStatus === 'rejected' ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-center text-red-600">
                            <XCircle className="w-4 h-4 mr-1" />
                            <span className="text-sm font-medium">MC Rejected</span>
                          </div>
                          {storedRejectionReason ? (
                            <div>
                              <p className="detail-label text-center">Reason</p>
                              <p className="detail-value detail-value-multiline text-center">{storedRejectionReason}</p>
                            </div>
                          ) : null}
                          <button onClick={onClose} className="btn btn-outline w-full">
                            <X className="w-4 h-4 mr-2" />
                            Close
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center justify-center text-green-600">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            <span className="text-sm font-medium">MC Processing Complete</span>
                          </div>
                          <button onClick={onClose} className="btn btn-outline w-full">
                            <X className="w-4 h-4 mr-2" />
                            Close
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {leaveStatus === 'submitted' ? (
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center justify-center text-orange-600">
                            <AlertTriangle className="w-4 h-4 mr-1" />
                            <span className="text-sm font-medium">MC Missing</span>
                          </div>
                          {showRejectForm ? (
                            <div className="space-y-2">
                              <label className="block text-xs font-medium text-gray-700">Reason for rejection</label>
                              <textarea
                                rows={3}
                                className="textarea-admin text-sm"
                                placeholder="Enter reason for rejection..."
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                              />
                              <div className="flex gap-3 btn-row-spread">
                                <button
                                  onClick={() => { setShowRejectForm(false); setRejectionReason(''); }}
                                  disabled={submitting}
                                  className="btn btn-outline flex-1"
                                >
                                  Back
                                </button>
                                <button
                                  onClick={() => handleValidateMc(false)}
                                  disabled={submitting || !rejectionReason.trim()}
                                  className="btn btn-danger flex-1"
                                >
                                  <XCircle className="w-4 h-4 mr-2" />
                                  {submitting ? 'Processing...' : 'Confirm Reject'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setShowRejectForm(true)}
                              disabled={submitting}
                              className="btn btn-danger w-full"
                              title="Reject application due to missing medical certificate"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Reject Application
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center text-orange-600">
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          <span className="text-sm font-medium">MC Not Provided</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Portal>

    <ConfirmModal
      isOpen={showCloseConfirm}
      title="Close Without Saving?"
      confirmLabel="Close"
      cancelLabel="Go Back"
      variant="danger"
      theme="admin"
      onConfirm={handleConfirmedClose}
      onCancel={() => setShowCloseConfirm(false)}
      isLoading={isDiscarding}
    >
      <div className="space-y-2">
        <p className="text-sm text-gray-700">
          The document has not been verified or rejected. Any field edits will be lost.
        </p>
        {isHRInitiated && (
          <p className="text-sm text-orange-700 font-medium">
            This draft application and its uploaded document will be permanently removed.
          </p>
        )}
      </div>
    </ConfirmModal>

    <ConfirmModal
      isOpen={showCancelConfirm}
      title="Cancel This Leave?"
      confirmLabel="Confirm Cancellation"
      cancelLabel="Go Back"
      variant="danger"
      theme="admin"
      onConfirm={handleCancelVerifiedLeave}
      onCancel={() => { setShowCancelConfirm(false); setCancellationReason(''); }}
      isLoading={cancelling}
      confirmDisabled={!cancellationReason.trim()}
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-700">
          This will mark the <strong>{displayLeaveTypeName}</strong>
          {employeeName ? <> for <strong>{employeeName}</strong></> : null}
          {displayStartDate && (
            <> ({formatDate(displayStartDate)}{displayEndDate ? ` – ${formatDate(displayEndDate)}` : ''})</>
          )}
          {' '}as <strong>Cancelled</strong> and restore the deducted leave balance.
        </p>
        <p className="text-sm text-gray-600">
          The medical certificate and its verification history are <strong>kept on record</strong> for audit —
          nothing is deleted.
        </p>
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-gray-700">
            Reason for cancellation <span className="text-red-600">*</span>
          </label>
          <textarea
            rows={3}
            className="textarea-admin text-sm"
            placeholder="e.g. Superseded by Hospitalisation Leave MC, duplicate entry, data-entry error..."
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            autoFocus
          />
          <p className="text-xs text-gray-500">Recorded in the audit log and the leave's approval timeline.</p>
        </div>
      </div>
    </ConfirmModal>
    </>
  );
};

export default MCDocumentVerificationModal;
